/**
 * PATH: apps/quote/src/components/builder/scope-sheet-grid/EditableCell.tsx
 * PURPOSE: Single editable cell within the scope sheet grid.
 *
 * KEYBOARD CONTRACT (Excel-like):
 *   Enter      → commit value, move down (parent handles via onKeyDown).
 *   Tab        → commit value, move right.
 *   Shift+Tab  → commit value, move left.
 *   Arrow keys → commit value, navigate.
 *   Escape     → discard edit (handled in-cell).
 *
 * Why explicit handlers despite synthetic-event bubbling:
 *   The grid root owns onKeyDown via spreadsheet.handleKeyDown, but relying on
 *   React event bubbling alone has produced "Enter does nothing" reports in the
 *   wild (qf builder Q10001/Q10002). Calling the parent handler directly here
 *   makes commit-and-navigate deterministic regardless of any focus-trap or
 *   stopPropagation higher up the tree.
 */

import { useMemo, useRef, useEffect } from 'react'
import { AlertCircle, Loader2, Sigma } from 'lucide-react'
import { cn, formatCurrency } from '@/lib/utils'
import type { CellErrorState } from '@/hooks/useBuilderEditsBuffer'
import { evaluateCell, isFormulaText, type FormulaContext } from '@/lib/quote-builder-math/formula'

interface EditableCellProps {
  field: string
  value: unknown
  optimisticValue?: unknown
  errorState?: CellErrorState
  isActive: boolean
  isEditing: boolean
  editValue: string
  onStartEdit: () => void
  onSetEditValue: (v: string) => void
  onCommit: (value: string) => void
  /**
   * Forwarded to the inner <input>. The grid root passes
   * spreadsheet.handleKeyDown here so Enter/Tab/Arrow commit + navigate
   * even when the input has focus and might otherwise swallow the event.
   */
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void
  type?: 'text' | 'number' | 'currency'
  align?: 'left' | 'center' | 'right'
  className?: string
  editable?: boolean
  /**
   * If set, this cell has a persisted "=expression" formula. Display mode
   * shows the evaluated value (or error sigil); edit mode shows the raw
   * formula text from `editValue`. Commit decisions live in the parent.
   */
  formula?: string | null
  /**
   * Variable resolution context for evaluating `formula` (and any =text the
   * user is currently typing in `editValue`). Keys are field names from the
   * line item (qty, pmpd, labour_chargeout, …); values may be string or
   * number — coerced internally.
   */
  formulaContext?: FormulaContext
}

export default function EditableCell({
  field,
  value,
  optimisticValue,
  errorState,
  isActive,
  isEditing,
  editValue,
  onStartEdit,
  onSetEditValue,
  onCommit,
  onKeyDown,
  type = 'text',
  align = 'left',
  className,
  editable = true,
  formula = null,
  formulaContext,
}: EditableCellProps) {
  const inputRef = useRef<HTMLInputElement>(null)

  const displayValue = optimisticValue !== undefined ? optimisticValue : value

  // If a formula is persisted, prefer its evaluated value for display.
  // Falls back gracefully when context is missing or eval fails — we then
  // show the cached numeric (last successful eval) or '-' if neither exist.
  const formulaEval = useMemo(() => {
    if (!formula || !isFormulaText(formula)) return null
    return evaluateCell(formula, formulaContext ?? {})
  }, [formula, formulaContext])

  const formatted = useMemo(() => {
    if (formulaEval) {
      if (formulaEval.ok) {
        if (type === 'currency') return formatCurrency(formulaEval.value)
        if (type === 'number') return formulaEval.value.toFixed(1)
        return String(formulaEval.value)
      }
      return formulaEval.error
    }
    if (displayValue == null || displayValue === '') return '-'
    if (type === 'currency') return formatCurrency(parseFloat(String(displayValue)) || 0)
    if (type === 'number') {
      const num = parseFloat(String(displayValue))
      return isNaN(num) ? '-' : num.toFixed(1)
    }
    return String(displayValue)
  }, [displayValue, type, formulaEval])

  // While typing, if the value starts with "=" the input must accept letters
  // and operators. We dynamically flip from <input type="number"> to "text"
  // for formulas (otherwise number inputs reject "=", "*", and identifiers).
  const editingFormula = isFormulaText(editValue)
  const inputType = editingFormula
    ? 'text'
    : type === 'number' || type === 'currency'
    ? 'number'
    : 'text'

  useEffect(() => {
    if (isEditing && isActive && inputRef.current) {
      inputRef.current.focus()
      inputRef.current.select()
    }
  }, [isEditing, isActive])

  const handleBlur = () => {
    if (editValue !== String(value || '')) {
      onCommit(editValue)
    }
  }

  /**
   * Cell-level keyboard:
   *   - Escape: revert the in-flight edit and let the grid handler bail out
   *     (we still forward to parent so it can flip isEditing=false).
   *   - Enter / Tab / Arrow: commit immediately, then forward to the grid
   *     handler which navigates. This keeps state consistent — without the
   *     explicit commit, the grid handler navigates BEFORE the input's blur
   *     fires its commit, and the typed value is lost.
   */
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const navKeys = new Set([
      'Enter',
      'Tab',
      'ArrowUp',
      'ArrowDown',
      'ArrowLeft',
      'ArrowRight',
    ])
    if (e.key === 'Escape') {
      onSetEditValue(String(value || ''))
      onKeyDown?.(e)
      return
    }
    if (navKeys.has(e.key)) {
      // Commit BEFORE handing off to grid nav so the typed value isn't lost
      // when the input unmounts on isEditing=false.
      if (editValue !== String(value ?? '')) {
        onCommit(editValue)
      }
      onKeyDown?.(e)
      return
    }
    onKeyDown?.(e)
  }

  const alignClass = { left: 'text-left', center: 'text-center', right: 'text-right' }[align]

  const hasError = errorState === 'error'
  const isRetrying = errorState === 'retrying'
  const hasOptimistic = optimisticValue !== undefined
  const hasFormula = !!formula && isFormulaText(formula)
  const formulaError = formulaEval && !formulaEval.ok

  return (
    <div
      className={cn(
        'relative h-full flex items-center px-1.5 min-w-0',
        alignClass,
        className,
        isActive && editable && 'ring-2 ring-inset ring-blue-400 bg-blue-50/50 dark:bg-blue-900/30',
        hasError && 'ring-1 ring-inset ring-red-400 bg-red-50 dark:bg-red-900/20',
        hasOptimistic && !hasError && 'bg-yellow-50/30 dark:bg-yellow-900/20',
        hasFormula && !formulaError && 'bg-violet-50/30 dark:bg-violet-900/20',
        formulaError && 'bg-red-50/40 dark:bg-red-900/30 text-red-700 dark:text-red-300',
      )}
      onMouseDown={(e) => {
        if (!editable) return
        e.stopPropagation()
        onStartEdit()
      }}
      title={hasFormula ? `Formula: ${formula}` : undefined}
    >
      {isEditing && isActive && editable ? (
        <input
          ref={inputRef}
          type={inputType}
          step={inputType === 'number' ? '0.01' : undefined}
          value={editValue}
          onChange={(e) => onSetEditValue(e.target.value)}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          className={cn(
            'w-full h-full bg-transparent outline-none text-[11px] tabular-nums',
            alignClass,
            editingFormula && 'font-mono text-violet-700 dark:text-violet-300',
          )}
        />
      ) : (
        <span className="truncate w-full tabular-nums text-[11px]">{formatted}</span>
      )}

      {hasFormula && !isEditing && !formulaError && (
        <Sigma className="w-3 h-3 text-violet-500 absolute left-0.5 top-1/2 -translate-y-1/2 opacity-60 pointer-events-none" />
      )}
      {hasError && <AlertCircle className="w-3 h-3 text-red-500 absolute right-0.5 top-1/2 -translate-y-1/2" />}
      {isRetrying && <Loader2 className="w-3 h-3 text-blue-500 animate-spin absolute right-0.5 top-1/2 -translate-y-1/2" />}
    </div>
  )
}
